import "./OpenEndedQuestionEditor.scss";

import { CalendarIcon, Flex, Menu } from "@fluentui/react-northstar";
import { FieldDescriptors } from "@shopify/react-form-state";
import { OpenEndedQuestion, QuestionContentType } from "@teams/types";
import React, { useMemo } from "react";

import { CheckboxField, DropdownItem, FieldLabel } from "../../../../_shared/components/fields";
import { PollyIcon } from "../../../../_shared/components/PollyIcon";
import { QuestionSettings } from "../../../../_shared/types";
import { AuthoringQuestionCollapseButton } from "./AuthoringQuestionCollapseButton";

type Props = {
  fields: FieldDescriptors<OpenEndedQuestion>;
  settings: QuestionSettings;
  isLimitedEdit?: boolean;
  setCollapsed?: (collapsed: boolean) => void;
};

export const OpenEndedQuestionEditor: React.FC<Props> = ({
  fields,
  settings,
  isLimitedEdit,
  setCollapsed,
}) => {
  const inputTypes = useMemo(() => {
    const types: DropdownItem<QuestionContentType>[] = [
      {
        label: "Text",
        value: "text/plain",
      },
      {
        label: "Date",
        value: "text/date",
      },
      {
        label: "Time",
        value: "text/time",
      },
    ];

    if (!settings.allowedContentTypes) {
      return types;
    }

    const filtered = types.filter(t => settings.allowedContentTypes?.includes(t.value));
    if (!filtered.some(t => t.value === fields.contentType.value) && filtered.length > 0) {
      fields.contentType.onChange(filtered[0].value);
    }
    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.allowedContentTypes, fields.contentType?.value]);

  if (inputTypes.length <= 1) {
    return null;
  }

  return (
    <Flex column gap="gap.medium">
      <Flex column gap="gap.smaller" hAlign="start">
        <FieldLabel label="Input type" weight="regular" tooltip="Type of user input" />
        <Menu
          className="open-ended-editor__type-menu"
          items={[
            {
              key: "text",
              icon: <PollyIcon icon="open-text-icon" inheritColor />,
              content: "Text",
              active: fields.contentType.value === "text/plain",
              onClick: () => fields.contentType.onChange("text/plain"),
              disabled: isLimitedEdit,
            },
            {
              key: "date",
              icon: <CalendarIcon />,
              content: "Date",
              active: fields.contentType.value === "text/date",
              onClick: () => fields.contentType.onChange("text/date"),
              disabled: isLimitedEdit,
            },
            {
              key: "time",
              icon: <PollyIcon icon="clock-icon" inheritColor />,
              content: "Time",
              active: fields.contentType.value === "text/time",
              onClick: () => fields.contentType.onChange("text/time"),
              disabled: isLimitedEdit,
            },
          ]}
        />
      </Flex>

      <Flex vAlign="end">
        {!settings.hideRequired && (
          <Flex.Item grow>
            <CheckboxField field={fields.required} trueLabel="Required" falseLabel="Required" />
          </Flex.Item>
        )}
        <Flex.Item push>
          <div>
            <AuthoringQuestionCollapseButton setCollapsed={setCollapsed} />
          </div>
        </Flex.Item>
      </Flex>
    </Flex>
  );
};
